import type { Banner, BannerListResponse, BannerQueryParams } from '../../models/BannerModel';
import BannerRepository from '../BannerRepository';
import { apiFetch } from '../../utils/apiFetch';

export default class BeatAPIBannerRepository extends BannerRepository {
  private _url: string;
  private _apiKey: string;

  constructor(url: string, apiKey: string) {
    super();
    this._url = url;
    this._apiKey = apiKey;
  }

  set url(url: string) {
    this._url = url;
  }

  set apiKey(apiKey: string) {
    this._apiKey = apiKey;
  }

  async getBanners(params: BannerQueryParams): Promise<Banner[]> {
    const query = new URLSearchParams();
    query.set('top_left[0]', String(params.top_left[0]));
    query.set('top_left[1]', String(params.top_left[1]));
    query.set('bottom_right[0]', String(params.bottom_right[0]));
    query.set('bottom_right[1]', String(params.bottom_right[1]));
    if (params.locale) {
      query.set('locale', params.locale);
    }
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((slug, i) => query.set(`tags[${i}]`, slug));
    }

    const response = await apiFetch(`${this._url}?${query.toString()}`, {
      method: 'GET',
      apiKey: this._apiKey,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch banners: ${response.status} ${response.statusText}`);
    }

    const data: BannerListResponse = await response.json();
    return data.data;
  }

  async recordImpression(bannerId: string, lat?: number, lng?: number): Promise<void> {
    await this.recordEvent(bannerId, 'impression', lat, lng);
  }

  async recordClick(bannerId: string, lat?: number, lng?: number): Promise<void> {
    await this.recordEvent(bannerId, 'click', lat, lng);
  }

  private async recordEvent(
    bannerId: string,
    eventType: 'impression' | 'click',
    lat?: number,
    lng?: number,
  ): Promise<void> {
    const body: Record<string, number> = {};
    if (lat != null) body.lat = lat;
    if (lng != null) body.lng = lng;

    await apiFetch(`${this._url}/${bannerId}/${eventType}`, {
      method: 'POST',
      apiKey: this._apiKey,
      body: JSON.stringify(body),
    });
  }
}
